import { Badge } from '@ordo/ui/badge'
import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Separator } from '@ordo/ui/separator'
import { Skeleton } from '@ordo/ui/skeleton'
import { BackLink } from 'compositions/BackLink'
import { LoadingWrapper } from 'compositions/LoadingWrapper'
import { Page } from 'compositions/Page'
import { useSend } from 'hooks/api'
import { CheckCircle, XCircle } from 'lucide-react'
import { useMemo } from 'react'
import { useParams } from 'react-router'
import type { RecipientEntry, SendRecord } from 'types/sends'

import { smsReplyNotice } from '../../lib/sender-options'
import { formatSendDate, StatusBadge } from './send-shared'

interface RecipientStatus extends RecipientEntry {
  success: boolean
  error: string | null
  providerMessageId: string | null
}

function buildRecipientStatuses(send: SendRecord): RecipientStatus[] {
  const successIds = new Set<string>()
  const messageMap = new Map<string, string>()
  for (const pm of send.providerMessages ?? []) {
    successIds.add(pm.contactId)
    messageMap.set(pm.contactId, pm.providerMessageId)
  }

  // Parse per-recipient errors from the error summary string
  // Format: "contactId1: message; contactId2: message"
  const errorMap = new Map<string, string>()
  if (send.error) {
    for (const segment of send.error.split('; ')) {
      const colonIdx = segment.indexOf(': ')
      if (colonIdx > 0) {
        errorMap.set(segment.slice(0, colonIdx), segment.slice(colonIdx + 2))
      }
    }
  }

  return send.recipients.map((r) => ({
    ...r,
    success: successIds.has(r.contactId),
    error: errorMap.get(r.contactId) ?? null,
    providerMessageId: messageMap.get(r.contactId) ?? null,
  }))
}

function RecipientTable({ statuses, method }: { statuses: RecipientStatus[]; method: string }) {
  return (
    <div className="overflow-auto rounded-md border border-border">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-border bg-muted/30">
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Status</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Name</th>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">
              {method === 'EMAIL' ? 'Email' : 'Phone'}
            </th>
            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Details</th>
          </tr>
        </thead>
        <tbody>
          {statuses.map((r) => {
            const name = [r.firstName, r.lastName].filter(Boolean).join(' ') || '(unnamed)'
            return (
              <tr key={r.contactId} className="border-b border-border last:border-0">
                <td className="px-3 py-1.5">
                  {r.success ? (
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  ) : (
                    <XCircle className="h-4 w-4 text-destructive" />
                  )}
                </td>
                <td className="px-3 py-1.5 text-xs">{name}</td>
                <td className="px-3 py-1.5 text-xs">{r.address}</td>
                <td className="px-3 py-1.5 text-xs">
                  {r.error ? (
                    <span className="text-destructive">{r.error}</span>
                  ) : r.providerMessageId ? (
                    <span className="font-mono text-muted-foreground">{r.providerMessageId}</span>
                  ) : (
                    <span className="text-muted-foreground/50">—</span>
                  )}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function SendDetailContent({ send }: { send: SendRecord }) {
  const recipientStatuses = useMemo(() => buildRecipientStatuses(send), [send])

  return (
    <Card>
      <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-2 space-y-0">
        <div className="flex items-center gap-3">
          <CardTitle>{send.scheduledCommunication?.name ?? send.template?.name ?? 'Transactional Send'}</CardTitle>
          <StatusBadge status={send.status} />
        </div>
      </CardHeader>
      <Separator />
      <CardContent className="space-y-6 pt-6">
        {/* Summary */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-0.5">
            <dt className="text-sm font-medium text-muted-foreground">Method</dt>
            <dd className="text-sm">{send.method}</dd>
          </div>
          <div className="space-y-0.5">
            <dt className="text-sm font-medium text-muted-foreground">Template</dt>
            <dd className="text-sm">{send.template?.name ?? '—'}</dd>
          </div>
          {send.renderedSubject && (
            <div className="space-y-0.5">
              <dt className="text-sm font-medium text-muted-foreground">Subject</dt>
              <dd className="text-sm">{send.renderedSubject}</dd>
            </div>
          )}
          <div className="space-y-0.5">
            <dt className="text-sm font-medium text-muted-foreground">Sent</dt>
            <dd className="text-sm">{formatSendDate(send.startedAt, { withSeconds: true })}</dd>
          </div>
        </div>

        {send.method === 'SMS' ? (
          <div className="space-y-1 text-sm">
            <p>SMS from: {send.smsFromNumber ?? 'Not recorded on this older send'}</p>
            <p className="text-xs text-muted-foreground">
              {smsReplyNotice({ isDefault: false, inboundApp: send.smsInboundApp === 'schedule' ? 'schedule' : null })}
            </p>
          </div>
        ) : null}

        {/* Counts */}
        <div className="flex flex-wrap gap-2">
          <Badge variant="secondary">
            {send.recipientCount} recipient{send.recipientCount !== 1 ? 's' : ''}
          </Badge>
          <Badge variant="secondary" className="bg-green-900/30 text-green-400">
            {send.successCount} delivered
          </Badge>
          {send.failureCount > 0 && (
            <Badge variant="secondary" className="bg-red-900/30 text-red-400">
              {send.failureCount} failed
            </Badge>
          )}
        </div>

        {send.error && (
          <>
            <Separator />
            <div className="space-y-1">
              <p className="text-sm font-medium text-destructive">Errors</p>
              <p className="break-all whitespace-pre-wrap rounded-md bg-destructive/10 px-3 py-2 font-mono text-xs text-destructive">
                {send.error}
              </p>
            </div>
          </>
        )}

        <Separator />

        {/* Recipient table */}
        <div className="space-y-3">
          <h3 className="text-sm font-medium text-muted-foreground">Recipients</h3>
          <RecipientTable statuses={recipientStatuses} method={send.method} />
        </div>
      </CardContent>
    </Card>
  )
}

export function SendDetail() {
  const { id } = useParams<{ id: string }>()
  const { data: send, loading } = useSend(id)

  if (!loading && !send) {
    return (
      <Page>
        <div className="py-12 text-center text-muted-foreground">Send record not found</div>
      </Page>
    )
  }

  return (
    <Page>
      <div className="mb-4">
        <BackLink to="/communications">Back to Messages</BackLink>
      </div>

      <LoadingWrapper
        loading={loading}
        loadingElement={
          <Card>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <Separator />
            <CardContent className="space-y-4 pt-6">
              <Skeleton className="h-4 w-32" />
              <Skeleton className="h-24 w-full" />
            </CardContent>
          </Card>
        }>
        {send && <SendDetailContent send={send} />}
      </LoadingWrapper>
    </Page>
  )
}
